import { useEffect, useRef, useState } from 'react'

type Options = {
  root?: Element | null
  /** fixe Navbar berücksichtigen, z.B. '-80px 0px -20% 0px' */
  rootMargin?: string
  /** ab welcher Sichtbarkeit soll EINFADEN passieren */
  enterThreshold?: number
  /** unter welcher Sichtbarkeit soll AUSFADEN passieren */
  exitThreshold?: number
  /** einmalig (true) oder bidirektional (false) */
  once?: boolean
}

export function useInView<T extends HTMLElement>({
  root = null,
  rootMargin = '-80px 0px -20% 0px',
  enterThreshold = 0.55,
  exitThreshold = 0.25,
  once = false,
}: Options = {}) {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)
  // 🔧 wichtig: aktueller Zustand als Ref, damit der Observer kein stale value nutzt
  const inViewRef = useRef(inView)

  useEffect(() => {
    inViewRef.current = inView
  }, [inView])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const ratio = entry.intersectionRatio

          // Einblenden (nur wenn aktuell nicht sichtbar)
          if (!inViewRef.current && ratio >= enterThreshold) {
            inViewRef.current = true
            setInView(true)
            if (once) observer.unobserve(entry.target)
          }

          // Ausblenden (nur wenn aktuell sichtbar und nicht "once")
          if (!once && inViewRef.current && ratio <= exitThreshold) {
            inViewRef.current = false
            setInView(false)
          }
        }
      },
      {
        root,
        rootMargin,
        // zwei Schwellen reichen; wir lesen intersectionRatio direkt
        threshold: [exitThreshold, enterThreshold],
      }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [root, rootMargin, enterThreshold, exitThreshold, once])

  return { ref, inView }
}
